import { type StateCreator } from 'zustand';

import { type AppState } from './store.js';

export type Theme = 'system' | 'light' | 'dark';
/** Row height in tables. Operators managing hundreds of rows ask for compact. */
export type Density = 'comfortable' | 'compact';

export interface UiSlice {
  readonly ui: {
    readonly sidebarCollapsed: boolean;
    readonly theme: Theme;
    readonly density: Density;
    /**
     * Last selected property id per resource.
     *
     * Remembers which property the user had chosen in list filters so the next
     * create screen for the same resource can pre-select it, and so the filter
     * is restored when they return to the list.
     *
     * Optional because a persisted store from before this field was added will
     * not contain it; selectors fall back to an empty string.
     */
    readonly lastPropertyIds?: Readonly<Record<string, string>> | undefined;
  };
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  setLastPropertyId: (resource: string, propertyId: string) => void;
}

export const createUiSlice: StateCreator<AppState, [], [], UiSlice> = (set) => ({
  ui: { sidebarCollapsed: false, theme: 'system', density: 'comfortable', lastPropertyIds: {} },

  toggleSidebar: () => {
    set((state) => ({ ui: { ...state.ui, sidebarCollapsed: !state.ui.sidebarCollapsed } }));
  },
  setSidebarCollapsed: (sidebarCollapsed) => {
    set((state) => ({ ui: { ...state.ui, sidebarCollapsed } }));
  },
  setTheme: (theme) => {
    set((state) => ({ ui: { ...state.ui, theme } }));
  },
  setDensity: (density) => {
    set((state) => ({ ui: { ...state.ui, density } }));
  },
  setLastPropertyId: (resource, propertyId) => {
    set((state) => ({
      ui: {
        ...state.ui,
        lastPropertyIds: { ...(state.ui.lastPropertyIds ?? {}), [resource]: propertyId },
      },
    }));
  },
});
